import React, {useMemo} from 'react';
import styles from './edit-form.module.scss';
import {Controller, FieldErrors, useForm} from 'react-hook-form';
import Yup from 'src/infra/Yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {useUpdateMaintenance} from './queries';
import Maintenance from 'src/core/models/maintenance';
import FormLayout from 'src/autobahn/layouts/form-layout';
import {Field} from 'src/autobahn/components/form/field';
import {TextInput} from 'src/autobahn/components/input/text-input';
import {NumberInput} from 'src/autobahn/components/input/number-input';
import {DateInput} from 'src/autobahn/components/input/date-input';
import {useIntl} from 'react-intl';

type FormData = {
  name: string;
  odometerValue?: number;
  dateValue?: Date;
  hourmeterValue?: number;
  intervalValue?: number;
}

interface MaintenanceEditFormProps {
  maintenance: Maintenance;
  onCancel: () => void;
}

const MaintenanceEditForm = ({maintenance, onCancel}: MaintenanceEditFormProps) => {
  const intl = useIntl();

  const schema = useMemo(() => {
    const shape: {[key: string]: Yup.AnySchema} = {
      name: Yup.string()
        .label(intl.formatMessage({id: 'sharedTitle'}))
        .required(),
    };

    const defaultValues: Partial<FormData> = {
      name: maintenance.name,
    };

    if (maintenance.odometerValue > 0) {
      shape.odometerValue = Yup.number()
        .label(intl.formatMessage({id: 'sharedOdometer'}))
        .required();

      defaultValues.odometerValue = maintenance.odometerValue;
    }

    if (maintenance.dateValue) {
      shape.dateValue = Yup.date()
        .label(intl.formatMessage({id: 'sharedDate'}))
        .typeError(intl.formatMessage({id: 'sharedDateInvalid'}))
        .required();

        defaultValues.dateValue = new Date(maintenance.dateValue);
    }

    if (maintenance.hourmeterValue > 0) {
      shape.hourmeterValue = Yup.number()
        .label(intl.formatMessage({id: 'sharedHourmeter'}))
        .required();

      defaultValues.hourmeterValue = maintenance.hourmeterValue;
    }

    if (maintenance.intervalValue > 0) {
      shape.hourmeterValue = Yup.number()
        .label(intl.formatMessage({id: 'sharedInterval'}))
        .required();

      defaultValues.intervalValue = maintenance.intervalValue;
    }

    const _schema = Yup.object().shape(shape) as unknown as Yup.ObjectSchema<FormData>;

    return {
      schema: _schema,
      defaultValues,
    };
  }, [maintenance]);

  const {control, register, handleSubmit} = useForm<FormData>({
    resolver: yupResolver(schema.schema, {abortEarly: false}),
    defaultValues: schema.defaultValues,
  });

  const {mutate: updateMaintenance} = useUpdateMaintenance();

  const onFormDataValid = (data: FormData) => {
    console.log('__form_success', data);
    updateMaintenance({
      ...maintenance,
      ...data,
    }, {
      onSuccess: () => {
        onCancel();
      },
    });
  };

  const onFormDataInvalid = (data: FieldErrors<FormData>) => {
    console.log('__form_error', data);
  };

  return (
    <FormLayout
      title={intl.formatMessage({id: 'sharedMaintenance'})}
      onCancel={onCancel}
      onSubmit={handleSubmit(onFormDataValid, onFormDataInvalid)}
    >
      <Field
        name='name'
        type='text'
        label={intl.formatMessage({id: 'sharedTitle'})}
      >
        <TextInput
          {...register('name')}
        />
      </Field>

      <div style={{display: 'flex', flex: 1, flexDirection: 'column', rowGap: '16px'}}>
        {!!maintenance.odometerValue && (
          <Field
            name='odometerValue'
            type='number'
            label={`${intl.formatMessage({id: 'sharedOdometer'})} (Km)`}
            labelKind='light'
            horizontal
            className={styles.halfWidth}
          >
            <NumberInput
              {...register('odometerValue')}
            />
          </Field>
        )}
        {!!maintenance.dateValue && (
          <Field
            type='date'
            name='dateValue'
            label={intl.formatMessage({id: 'sharedDate'})}
            labelKind='light'
            horizontal
            className={styles.halfWidth}
          >
            <Controller
              control={control}
              name='dateValue'
              render={({field}) => (
                <DateInput
                  {...field}
                  displayFormat='dd/MM/yyyy'
                />
              )}
            />
          </Field>
        )}
        {!!maintenance.hourmeterValue && (
          <Field
            type='number'
            name='hourmeterValue'
            label={`${intl.formatMessage({id: 'sharedHourmeter'})} (${intl.formatMessage({id: 'sharedHours'})})`}
            labelKind='light'
            horizontal
            className={styles.halfWidth}
          >
            <NumberInput
              {...register('hourmeterValue')}
            />
          </Field>
        )}
        {!!maintenance.intervalValue && (
          <Field
            type='number'
            name='intervalValue'
            label={`${intl.formatMessage({id: 'sharedInterval'})} (${intl.formatMessage({id: 'sharedMonths'})})`}
            labelKind='light'
            horizontal
            className={styles.halfWidth}
          >
            <NumberInput
              {...register('intervalValue')}
            />
          </Field>
        )}
      </div>
    </FormLayout>
  );
};

export default MaintenanceEditForm;
